import { useEffect, useRef, useState, useCallback } from "react";
import { useMap, useMapEvents, Polyline, CircleMarker, Tooltip } from "react-leaflet";
import L from "leaflet";

/**
 * MeasureTool – draws polylines on the map to measure distances in meters.
 * Uses the Haversine formula via Leaflet's latLng.distanceTo().
 *
 * Props:
 *  - enabled: boolean – whether measure mode is active
 *  - onTotalChange: (meters) => void – optional callback with total distance
 */
export default function MeasureTool({ enabled, onTotalChange, onDrawingChange, doneSignal, undoSignal }) {
  const map = useMap();
  const [points, setPoints] = useState([]);
  const [cursorPos, setCursorPos] = useState(null);
  const [drawing, setDrawing] = useState(true); // actively placing points
  const prevEnabled = useRef(enabled);
  const prevDoneSignal = useRef(doneSignal || 0);
  const prevUndoSignal = useRef(undoSignal || 0);

  // Notify parent of drawing state changes
  useEffect(() => {
    if (onDrawingChange) onDrawingChange(drawing);
  }, [drawing, onDrawingChange]);

  // External "done" signal — stop drawing when parent increments doneSignal
  useEffect(() => {
    if (doneSignal && doneSignal !== prevDoneSignal.current && drawing) {
      setDrawing(false);
      setCursorPos(null);
    }
    prevDoneSignal.current = doneSignal || 0;
  }, [doneSignal, drawing]);

  // External "undo" signal — remove last point when parent increments undoSignal
  useEffect(() => {
    if (undoSignal && undoSignal !== prevUndoSignal.current && drawing) {
      setPoints((prev) => prev.slice(0, -1));
    }
    prevUndoSignal.current = undoSignal || 0;
  }, [undoSignal, drawing]);

  // Reset when disabled (toggle off or X button)
  useEffect(() => {
    if (prevEnabled.current && !enabled) {
      setPoints([]);
      setCursorPos(null);
      setDrawing(true);
      if (onTotalChange) onTotalChange(0);
    }
    // Re-enable drawing when turned on fresh
    if (!prevEnabled.current && enabled) {
      setDrawing(true);
    }
    prevEnabled.current = enabled;
  }, [enabled, onTotalChange]);

  // ESC key: stop drawing but keep lines visible
  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && drawing) {
        setDrawing(false);
        setCursorPos(null);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled, drawing]);

  // Change cursor style — crosshair only while drawing
  useEffect(() => {
    const container = map.getContainer();
    if (enabled && drawing) {
      container.style.cursor = "crosshair";
    } else {
      container.style.cursor = "";
    }
    return () => { container.style.cursor = ""; };
  }, [enabled, drawing, map]);

  // Calculate total distance
  const calcTotal = useCallback((pts) => {
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
      total += L.latLng(pts[i - 1]).distanceTo(L.latLng(pts[i]));
    }
    return total;
  }, []);

  // Report total when points change
  useEffect(() => {
    if (onTotalChange) onTotalChange(calcTotal(points));
  }, [points, calcTotal, onTotalChange]);

  // Map event handlers — only active while drawing
  useMapEvents({
    click(e) {
      if (!enabled || !drawing) return;
      // Ignore clicks on popups/controls
      if (e.originalEvent?.target?.closest(".leaflet-popup, .leaflet-control, .measure-panel")) return;
      const pt = [e.latlng.lat, e.latlng.lng];
      setPoints((prev) => [...prev, pt]);
    },
    mousemove(e) {
      if (!enabled || !drawing || points.length === 0) return;
      setCursorPos([e.latlng.lat, e.latlng.lng]);
    },
    contextmenu(e) {
      if (!enabled || !drawing) return;
      // Right-click to undo last point
      e.originalEvent.preventDefault();
      setPoints((prev) => prev.slice(0, -1));
    },
  });

  if (!enabled && points.length === 0) return null;

  const totalDist = calcTotal(points);
  // Segment from last point to cursor (only while drawing)
  const previewLine = drawing && points.length > 0 && cursorPos ? [points[points.length - 1], cursorPos] : null;
  const previewDist = previewLine ? L.latLng(previewLine[0]).distanceTo(L.latLng(previewLine[1])) : 0;

  return (
    <>
      {/* Confirmed polyline */}
      {points.length >= 2 && (
        <Polyline
          positions={points}
          pathOptions={{ color: "#f97316", weight: 3, dashArray: "6 4" }}
        />
      )}
      {/* Preview line to cursor */}
      {drawing && enabled && previewLine && (
        <Polyline
          positions={previewLine}
          pathOptions={{ color: "#f97316", weight: 2, opacity: 0.6, dashArray: "4 4" }}
        />
      )}
      {/* Vertex markers */}
      {points.map((pt, i) => (
        <CircleMarker
          key={i}
          center={pt}
          radius={4}
          pathOptions={{ color: "#f97316", fillColor: "#fff", fillOpacity: 1, weight: 2 }}
        >
          {i === points.length - 1 && points.length >= 2 && (
            <Tooltip permanent direction="top" offset={[0, -8]} className="measure-tooltip">
              <span className="text-[11px] font-semibold">{formatDist(totalDist)}</span>
            </Tooltip>
          )}
        </CircleMarker>
      ))}
      {/* Preview distance near cursor */}
      {drawing && enabled && cursorPos && points.length > 0 && (
        <CircleMarker
          center={cursorPos}
          radius={3}
          pathOptions={{ color: "#f97316", fillColor: "#f97316", fillOpacity: 0.5, weight: 1 }}
        >
          <Tooltip permanent direction="right" offset={[8, 0]} className="measure-tooltip">
            <span className="text-[10px] text-gray-600">
              +{formatDist(previewDist)} = {formatDist(totalDist + previewDist)}
            </span>
          </Tooltip>
        </CircleMarker>
      )}
    </>
  );
}

function formatDist(meters) {
  if (meters < 1000) return `${meters.toFixed(1)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
}
